import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import AppleSection from "@/components/AppleSection";
import AppleSplitComparison from "@/components/AppleSplitComparison";
import Footer from "@/components/Footer";
import BusinessReportForm from "@/components/BusinessReportForm";
import { getAllClassifiedTypes } from "@/utils/businessClassifier";
import { blogPosts } from "@/data/blogPosts";
import { typography } from "@/styles/typography";

// Función para normalizar URLs eliminando acentos
const normalizeUrl = (str: string): string => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

// Meta información SEO por tipo de negocio
const seoMeta: Record<string, { title: string; description: string; keywords: string }> = {
  agencias: {
    title: "IA para Agencias de Marketing | Globaltis IA - Automatización Inteligente",
    description: "Multiplica la producción de tu agencia con IA. Consigue más clientes, escala sin contratar y elimina tareas administrativas. Resultados en semanas.",
    keywords: "ia para agencias, automatización marketing, agencia marketing digital, inteligencia artificial marketing"
  },
  restaurantes: {
    title: "IA para Restaurantes | Globaltis IA - Reservas Automáticas 24/7",
    description: "Nunca pierdas una reserva. IA que atiende llamadas 24/7, gestiona cancelaciones y fideliza clientes automáticamente. Más mesas llenas.",
    keywords: "ia para restaurantes, reservas automáticas, gestión restaurante, automatización hostelería"
  },
  ecommerce: {
    title: "IA para Ecommerce | Globaltis IA - Recupera Carritos Abandonados",
    description: "Recupera hasta 25% de carritos abandonados con IA. Atención al cliente 24/7 y contenido automático que vende. Más conversión, menos trabajo.",
    keywords: "ia para ecommerce, recuperar carritos, tienda online, automatización ventas"
  },
  consultoras: {
    title: "IA para Consultoras | Globaltis IA - Informes en Minutos",
    description: "Genera informes en minutos, no días. IA que analiza datos, redacta propuestas y libera tiempo para lo que realmente importa. Más proyectos, más rentabilidad.",
    keywords: "ia para consultoras, automatización consultoría, informes automáticos, consultora estrategia"
  },
  despachos: {
    title: "IA para Despachos de Abogados | Globaltis IA - Documentación Automática",
    description: "Reduce 70% el tiempo en documentación legal. IA que genera borradores, gestiona plazos y mantiene informados a tus clientes automáticamente.",
    keywords: "ia para despachos, automatización legal, gestión despacho, abogados ia"
  },
  inmobiliarias: {
    title: "IA para Inmobiliarias | Globaltis IA - Respuestas Instantáneas",
    description: "Responde leads en menos de 1 minuto. IA que cualifica, agenda visitas y crea contenido automáticamente. Más visitas, más cierres.",
    keywords: "ia para inmobiliarias, captación leads, gestión inmobiliaria, automatización inmobiliaria"
  },
  franquicias: {
    title: "IA para Franquicias | Globaltis IA - Control Total de tu Red",
    description: "Escala tu red sin aumentar equipo. IA que garantiza cumplimiento de estándares, automatiza formación y da visibilidad total de cada punto.",
    keywords: "ia para franquicias, gestión franquicia, expansión franquicia, automatización franquicia"
  },
  tiendas: {
    title: "IA para Tiendas | Globaltis IA - Inventario Inteligente",
    description: "Reduce 40% las roturas de stock y aumenta 25% las ventas. IA que predice demanda, fideliza clientes y gestiona tu presencia online.",
    keywords: "ia para tiendas, gestión inventario, comercio minorista, automatización retail"
  },
  gestorias: {
    title: "IA para Gestorías | Globaltis IA - Documentación en Minutos",
    description: "Procesa el doble de declaraciones con el mismo equipo. IA que automatiza documentación, cumple plazos y mantiene informados a tus clientes 24/7.",
    keywords: "ia para gestorías, automatización gestoría, gestión administrativa, gestoría automática"
  },
  asesorias: {
    title: "IA para Asesorías | Globaltis IA - De Rutina a Estrategia",
    description: "Convierte el 70% de tiempo rutinario en trabajo estratégico. IA que genera informes, detecta problemas y triplica tu capacidad de atención.",
    keywords: "ia para asesorías, automatización asesoría, asesoría fiscal, informes automáticos"
  },
  clinicas: {
    title: "IA para Clínicas | Globaltis IA - Reduce 80% los No-Shows",
    description: "Agenda siempre llena con IA. Recordatorios automáticos, atención 24/7 y fidelización de pacientes. Factura más sin ampliar horarios.",
    keywords: "ia para clínicas, gestión clínica, citas médicas, automatización salud"
  },
  hoteles: {
    title: "IA para Hoteles | Globaltis IA - Más Reservas Directas",
    description: "Aumenta 40% las reservas directas con IA. Atención 24/7, mejor experiencia de huéspedes y operaciones optimizadas. Menos comisiones OTA.",
    keywords: "ia para hoteles, reservas directas, gestión hotelera, automatización hotelería"
  },
  gimnasios: {
    title: "IA para Gimnasios | Globaltis IA - Reduce 40% las Bajas",
    description: "Socios más comprometidos con IA. Detecta abandonos antes de que ocurran, capta leads 24/7 y triplica el engagement de tu comunidad.",
    keywords: "ia para gimnasios, retención socios, gestión gym, automatización fitness"
  },
  academias: {
    title: "IA para Academias | Globaltis IA - Reduce 50% los Abandonos",
    description: "Triplica inscripciones y reduce abandonos a la mitad. IA que detecta alumnos en riesgo, responde consultas 24/7 y automatiza marketing.",
    keywords: "ia para academias, gestión academia, retención alumnos, automatización educación"
  }
};

interface SolutionBlock {
  title: string;
  bigNumber: string;
  comparison: string;
  description: string;
  ctaText: string;
  ctaLink: string;
}

interface CaseStudy {
  quote: string;
  author: string;
  role: string;
  metrics: Array<{ value: string; label: string }>;
}

interface ProcessStep {
  number: string;
  title: string;
  description: string;
}

interface FinalCTA {
  question: string;
  options: Array<{ text: string; link: string }>;
}

const businessConfig: Record<string, {
  hero: {
    title: string;
    subtitle: string;
  };
  problem: {
    bigStat: string;
    context: string;
  };
  blocks: SolutionBlock[];
  caseStudy: CaseStudy;
  process: ProcessStep[];
  comparison: Array<{ before: string; after: string }>;
  finalCTA: FinalCTA;
}> = {
  agencias: {
    hero: {
      title: "IA para agencias.",
      subtitle: "Menos horas. Más margen. Más clientes."
    },
    problem: {
      bigStat: "El 70% del tiempo",
      context: "lo pasas en tareas que no facturan."
    },
    blocks: [
      {
        title: "Consigue clientes mientras duermes",
        bigNumber: "×10 leads",
        comparison: "Con el mismo presupuesto",
        description: "La IA encuentra, cualifica y contacta clientes potenciales por ti. Tú solo cierras.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Escala sin contratar",
        bigNumber: "1 persona = 10",
        comparison: "En capacidad de producción",
        description: "Multiplica la producción de tu equipo sin aumentar plantilla. Videos, diseños, copys. Todo más rápido.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Olvídate de la gestión",
        bigNumber: "-60% tiempo",
        comparison: "En tareas administrativas",
        description: "Automatiza lo repetitivo. Informes, seguimientos, facturas. Tu equipo se enfoca en lo que factura.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      }
    ],
    caseStudy: {
      quote: "Triplicamos la producción sin aumentar equipo. La IA nos permitió enfocarnos en lo que realmente importa: los clientes.",
      author: "Director de Agencia",
      role: "Agencia de Marketing Digital",
      metrics: [
        { value: "×3", label: "Producción" },
        { value: "-60%", label: "Gestión" },
        { value: "0", label: "Contrataciones" }
      ]
    },
    process: [
      {
        number: "1",
        title: "Cuéntanos qué te frena",
        description: "Una llamada de 15 minutos"
      },
      {
        number: "2",
        title: "Te mostramos la solución",
        description: "Una demo con tu caso real"
      },
      {
        number: "3",
        title: "Lo implementamos juntos",
        description: "Resultados en semanas, no meses"
      }
    ],
    comparison: [
      { before: "Buscar leads manualmente", after: "Llegan solos" },
      { before: "1 video = 1 día", after: "1 video = 1 hora" },
      { before: "Información dispersa", after: "Todo centralizado" }
    ],
    finalCTA: {
      question: "¿Listo para multiplicar tu agencia?",
      options: [
        { text: "Agendar llamada", link: "/contacto" }
      ]
    }
  },
  restaurantes: {
    hero: {
      title: "IA para restaurantes.",
      subtitle: "Más reservas. Menos cancelaciones. Clientes que vuelven."
    },
    problem: {
      bigStat: "El 40% de las llamadas",
      context: "se pierden porque nadie puede contestar."
    },
    blocks: [
      {
        title: "Nunca pierdas una reserva",
        bigNumber: "24/7",
        comparison: "Reservas automáticas incluso a las 3AM",
        description: "La IA atiende llamadas, confirma reservas y gestiona cancelaciones. Tú te centras en la cocina.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Clientes que vuelven",
        bigNumber: "+35% repetición",
        comparison: "Con comunicación personalizada",
        description: "Mensajes automáticos en el momento justo. Cumpleaños, promociones, recordatorios. Sin que levantes un dedo.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Redes que se gestionan solas",
        bigNumber: "0 horas",
        comparison: "Dedicadas a crear contenido",
        description: "Fotos de platos convertidas en posts profesionales. Stories, reels y respuestas a comentarios. Todo automático.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      }
    ],
    caseStudy: {
      quote: "Pasamos de perder el 30% de llamadas a no perder ninguna. Y las reservas online se duplicaron.",
      author: "Propietario de Restaurante",
      role: "Restaurante de cocina mediterránea",
      metrics: [
        { value: "+45%", label: "Reservas" },
        { value: "0", label: "Llamadas perdidas" },
        { value: "+35%", label: "Clientes recurrentes" }
      ]
    },
    process: [
      {
        number: "1",
        title: "Cuéntanos tu situación",
        description: "Una llamada de 15 minutos"
      },
      {
        number: "2",
        title: "Te mostramos la solución",
        description: "Una demo con tu caso real"
      },
      {
        number: "3",
        title: "Lo implementamos juntos",
        description: "Resultados en semanas, no meses"
      }
    ],
    comparison: [
      { before: "Perder llamadas en hora punta", after: "Atención 24/7" },
      { before: "Clientes que no vuelven", after: "Comunicación automática" },
      { before: "Redes sociales abandonadas", after: "Contenido constante" }
    ],
    finalCTA: {
      question: "¿Listo para llenar más mesas?",
      options: [
        { text: "Agendar llamada", link: "/contacto" }
      ]
    }
  },
  ecommerce: {
    hero: {
      title: "IA para ecommerce.",
      subtitle: "Más ventas. Menos carritos abandonados. Clientes fieles."
    },
    problem: {
      bigStat: "El 70% de los carritos",
      context: "se abandonan antes de pagar."
    },
    blocks: [
      {
        title: "Recupera ventas perdidas",
        bigNumber: "+25% conversión",
        comparison: "En carritos abandonados",
        description: "La IA contacta al cliente en el momento justo, con el mensaje perfecto. Automáticamente.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Atención al cliente que no duerme",
        bigNumber: "24/7",
        comparison: "Respuestas instantáneas",
        description: "Dudas sobre productos, estado de pedidos, devoluciones. Todo resuelto al instante, sin que intervengas.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Contenido que vende",
        bigNumber: "×10 productos",
        comparison: "Descritos en el mismo tiempo",
        description: "Fichas de producto, emails, anuncios. Todo el contenido que necesitas, generado automáticamente.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      }
    ],
    caseStudy: {
      quote: "Recuperamos el 20% de los carritos abandonados. Eso son miles de euros al mes que antes perdíamos.",
      author: "Fundador de Ecommerce",
      role: "Tienda online de moda",
      metrics: [
        { value: "+20%", label: "Carritos recuperados" },
        { value: "-80%", label: "Tiempo en soporte" },
        { value: "+35%", label: "Ventas totales" }
      ]
    },
    process: [
      {
        number: "1",
        title: "Cuéntanos tu situación",
        description: "Una llamada de 15 minutos"
      },
      {
        number: "2",
        title: "Te mostramos la solución",
        description: "Una demo con tu caso real"
      },
      {
        number: "3",
        title: "Lo implementamos juntos",
        description: "Resultados en semanas, no meses"
      }
    ],
    comparison: [
      { before: "Carritos abandonados", after: "Ventas recuperadas" },
      { before: "Soporte colapsado", after: "Respuestas instantáneas" },
      { before: "Fichas de producto lentas", after: "Contenido automático" }
    ],
    finalCTA: {
      question: "¿Listo para vender más?",
      options: [
        { text: "Agendar llamada", link: "/contacto" }
      ]
    }
  },
  consultoras: {
    hero: {
      title: "IA para consultoras.",
      subtitle: "Más proyectos. Menos horas. Mayor rentabilidad."
    },
    problem: {
      bigStat: "El 60% del tiempo",
      context: "lo dedicas a tareas que no aportan valor al cliente."
    },
    blocks: [
      {
        title: "Informes en minutos, no días",
        bigNumber: "-80% tiempo",
        comparison: "En elaboración de informes",
        description: "La IA analiza datos, genera insights y redacta informes. Tú solo revisas y presentas.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Todo el conocimiento accesible",
        bigNumber: "1 pregunta",
        comparison: "Para encontrar cualquier información",
        description: "Proyectos anteriores, metodologías, plantillas. Todo tu conocimiento disponible al instante.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Propuestas que cierran",
        bigNumber: "×3 propuestas",
        comparison: "En el mismo tiempo",
        description: "Genera propuestas personalizadas en minutos. Más oportunidades, más cierres, más facturación.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      }
    ],
    caseStudy: {
      quote: "Antes tardábamos 2 días en un informe. Ahora 2 horas. Y la calidad es igual o mejor.",
      author: "Socio Director",
      role: "Consultora de estrategia",
      metrics: [
        { value: "-80%", label: "Tiempo en informes" },
        { value: "+50%", label: "Proyectos gestionados" },
        { value: "×2", label: "Rentabilidad" }
      ]
    },
    process: [
      {
        number: "1",
        title: "Cuéntanos qué te frena",
        description: "Una llamada de 15 minutos"
      },
      {
        number: "2",
        title: "Te mostramos la solución",
        description: "Una demo con tu caso real"
      },
      {
        number: "3",
        title: "Lo implementamos juntos",
        description: "Resultados en semanas, no meses"
      }
    ],
    comparison: [
      { before: "Informes de 2 días", after: "Informes de 2 horas" },
      { before: "Buscar información dispersa", after: "Todo en un solo lugar" },
      { before: "Propuestas genéricas", after: "Propuestas personalizadas" }
    ],
    finalCTA: {
      question: "¿Listo para multiplicar tu capacidad?",
      options: [
        { text: "Agendar llamada", link: "/contacto" }
      ]
    }
  },
  despachos: {
    hero: {
      title: "IA para despachos.",
      subtitle: "Menos papeleo. Más casos. Clientes satisfechos."
    },
    problem: {
      bigStat: "El 50% del tiempo",
      context: "lo dedicas a tareas administrativas, no a tus clientes."
    },
    blocks: [
      {
        title: "Documentos en minutos",
        bigNumber: "-70% tiempo",
        comparison: "En redacción de contratos y escritos",
        description: "La IA genera borradores, revisa documentos y organiza expedientes. Tú te centras en la estrategia legal.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Nunca olvides un plazo",
        bigNumber: "0 vencimientos",
        comparison: "Perdidos por descuido",
        description: "Recordatorios automáticos, seguimiento de casos y alertas inteligentes. Todo bajo control sin esfuerzo.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Clientes siempre informados",
        bigNumber: "24/7",
        comparison: "Actualizaciones automáticas",
        description: "Respuestas inmediatas sobre el estado de sus casos. Sin llamadas constantes ni emails pendientes.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      }
    ],
    caseStudy: {
      quote: "Redujimos el tiempo de preparación de casos a la mitad. Ahora podemos atender más clientes sin perder calidad.",
      author: "Socio Director",
      role: "Despacho de abogados",
      metrics: [
        { value: "-50%", label: "Tiempo administrativo" },
        { value: "+40%", label: "Casos gestionados" },
        { value: "0", label: "Plazos perdidos" }
      ]
    },
    process: [
      {
        number: "1",
        title: "Cuéntanos qué te frena",
        description: "Una llamada de 15 minutos"
      },
      {
        number: "2",
        title: "Te mostramos la solución",
        description: "Una demo con tu caso real"
      },
      {
        number: "3",
        title: "Lo implementamos juntos",
        description: "Resultados en semanas, no meses"
      }
    ],
    comparison: [
      { before: "Redacción manual de documentos", after: "Borradores automáticos" },
      { before: "Plazos perdidos", after: "Alertas inteligentes" },
      { before: "Clientes llamando constantemente", after: "Actualizaciones automáticas" }
    ],
    finalCTA: {
      question: "¿Listo para optimizar tu despacho?",
      options: [
        { text: "Agendar llamada", link: "/contacto" }
      ]
    }
  },
  inmobiliarias: {
    hero: {
      title: "IA para inmobiliarias.",
      subtitle: "Más visitas. Más cierres. Menos trabajo manual."
    },
    problem: {
      bigStat: "El 80% de leads",
      context: "se pierden por falta de seguimiento rápido."
    },
    blocks: [
      {
        title: "Responde al instante",
        bigNumber: "<1 minuto",
        comparison: "Tiempo de respuesta a consultas",
        description: "La IA atiende consultas, cualifica leads y agenda visitas. 24 horas, 7 días. Sin que pierdas ninguna oportunidad.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Encuentra la propiedad perfecta",
        bigNumber: "×5 matches",
        comparison: "Propiedades recomendadas por cliente",
        description: "Cruce inteligente entre lo que busca el cliente y tu cartera. Sugerencias personalizadas automáticas.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Contenido que vende",
        bigNumber: "0 horas",
        comparison: "Creando descripciones y anuncios",
        description: "Fichas de inmuebles, posts para redes, emails de seguimiento. Todo generado automáticamente.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      }
    ],
    caseStudy: {
      quote: "Antes respondíamos en horas, ahora en segundos. Las visitas se duplicaron y los cierres también.",
      author: "Director Comercial",
      role: "Inmobiliaria",
      metrics: [
        { value: "×2", label: "Visitas agendadas" },
        { value: "+60%", label: "Tasa de cierre" },
        { value: "-80%", label: "Tiempo en admin" }
      ]
    },
    process: [
      {
        number: "1",
        title: "Cuéntanos qué te frena",
        description: "Una llamada de 15 minutos"
      },
      {
        number: "2",
        title: "Te mostramos la solución",
        description: "Una demo con tu caso real"
      },
      {
        number: "3",
        title: "Lo implementamos juntos",
        description: "Resultados en semanas, no meses"
      }
    ],
    comparison: [
      { before: "Respuesta en horas", after: "Respuesta en segundos" },
      { before: "Leads perdidos", after: "Seguimiento automático" },
      { before: "Fichas manuales", after: "Contenido generado" }
    ],
    finalCTA: {
      question: "¿Listo para vender más propiedades?",
      options: [
        { text: "Agendar llamada", link: "/contacto" }
      ]
    }
  },
  franquicias: {
    hero: {
      title: "IA para franquicias.",
      subtitle: "Más control. Menos fricción. Crecimiento escalable."
    },
    problem: {
      bigStat: "El 60% de franquiciados",
      context: "no siguen los procesos porque son complicados."
    },
    blocks: [
      {
        title: "Operaciones uniformes",
        bigNumber: "100%",
        comparison: "Cumplimiento de estándares",
        description: "La IA guía a cada franquiciado paso a paso. Checklists automáticos, alertas y supervisión sin microgestión.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Formación continua",
        bigNumber: "-70% tiempo",
        comparison: "En onboarding de nuevos franquiciados",
        description: "Manuales interactivos, respuestas instantáneas a dudas y seguimiento personalizado. Todo automatizado.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Visibilidad total",
        bigNumber: "1 dashboard",
        comparison: "Para ver toda la red",
        description: "Métricas de cada punto en tiempo real. Detecta problemas antes de que escalen.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      }
    ],
    caseStudy: {
      quote: "Pasamos de 10 a 25 franquicias sin aumentar el equipo central. La IA nos dio el control que necesitábamos.",
      author: "Director de Expansión",
      role: "Red de franquicias",
      metrics: [
        { value: "×2.5", label: "Franquicias" },
        { value: "0", label: "Equipo adicional" },
        { value: "+40%", label: "Satisfacción red" }
      ]
    },
    process: [
      {
        number: "1",
        title: "Cuéntanos qué te frena",
        description: "Una llamada de 15 minutos"
      },
      {
        number: "2",
        title: "Te mostramos la solución",
        description: "Una demo con tu caso real"
      },
      {
        number: "3",
        title: "Lo implementamos juntos",
        description: "Resultados en semanas, no meses"
      }
    ],
    comparison: [
      { before: "Procesos inconsistentes", after: "Operaciones uniformes" },
      { before: "Onboarding de semanas", after: "Onboarding de días" },
      { before: "Problemas invisibles", after: "Visibilidad total" }
    ],
    finalCTA: {
      question: "¿Listo para escalar tu red?",
      options: [
        { text: "Agendar llamada", link: "/contacto" }
      ]
    }
  },
  tiendas: {
    hero: {
      title: "IA para tiendas.",
      subtitle: "Más ventas. Mejor inventario. Clientes fieles."
    },
    problem: {
      bigStat: "El 30% del stock",
      context: "se queda sin vender por mala gestión del inventario."
    },
    blocks: [
      {
        title: "Inventario inteligente",
        bigNumber: "-40% roturas",
        comparison: "De stock",
        description: "La IA predice qué vas a vender y cuándo. Pedidos automáticos, alertas de reposición. Sin sorpresas.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Clientes que vuelven",
        bigNumber: "+35%",
        comparison: "Repetición de compra",
        description: "Mensajes personalizados, ofertas en el momento justo, programa de fidelización automático.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Presencia online sin esfuerzo",
        bigNumber: "0 horas",
        comparison: "Gestionando redes",
        description: "Fotos de productos convertidas en contenido profesional. Google, Instagram, WhatsApp. Todo sincronizado.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      }
    ],
    caseStudy: {
      quote: "Redujimos el stock muerto a la mitad y las ventas crecieron un 25%. Sin contratar a nadie.",
      author: "Propietario",
      role: "Tienda de moda",
      metrics: [
        { value: "-50%", label: "Stock muerto" },
        { value: "+25%", label: "Ventas" },
        { value: "+35%", label: "Clientes recurrentes" }
      ]
    },
    process: [
      {
        number: "1",
        title: "Cuéntanos qué te frena",
        description: "Una llamada de 15 minutos"
      },
      {
        number: "2",
        title: "Te mostramos la solución",
        description: "Una demo con tu caso real"
      },
      {
        number: "3",
        title: "Lo implementamos juntos",
        description: "Resultados en semanas, no meses"
      }
    ],
    comparison: [
      { before: "Stock muerto del 30%", after: "Rotación optimizada" },
      { before: "Clientes de paso", after: "Clientes recurrentes" },
      { before: "Sin presencia online", after: "Contenido automático" }
    ],
    finalCTA: {
      question: "¿Listo para hacer crecer tu tienda?",
      options: [
        { text: "Agendar llamada", link: "/contacto" }
      ]
    }
  },
  gestorias: {
    hero: {
      title: "IA para gestorías.",
      subtitle: "Más clientes. Menos errores. Plazos siempre al día."
    },
    problem: {
      bigStat: "El 40% del tiempo",
      context: "se va en tareas repetitivas que podrían automatizarse."
    },
    blocks: [
      {
        title: "Documentación automática",
        bigNumber: "-60% tiempo",
        comparison: "En preparar documentos",
        description: "La IA extrae datos, rellena formularios y prepara declaraciones. Tú solo revisas y envías.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Cero plazos perdidos",
        bigNumber: "100%",
        comparison: "Cumplimiento de obligaciones",
        description: "Calendario inteligente con alertas, recordatorios a clientes y seguimiento automático de trámites.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Clientes siempre informados",
        bigNumber: "24/7",
        comparison: "Consultas resueltas",
        description: "Respuestas automáticas sobre el estado de sus trámites. Sin llamadas ni emails pendientes.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      }
    ],
    caseStudy: {
      quote: "Procesamos el doble de declaraciones con el mismo equipo. Y sin errores.",
      author: "Gerente",
      role: "Gestoría administrativa",
      metrics: [
        { value: "×2", label: "Capacidad" },
        { value: "0", label: "Errores en plazos" },
        { value: "-60%", label: "Trabajo manual" }
      ]
    },
    process: [
      {
        number: "1",
        title: "Cuéntanos qué te frena",
        description: "Una llamada de 15 minutos"
      },
      {
        number: "2",
        title: "Te mostramos la solución",
        description: "Una demo con tu caso real"
      },
      {
        number: "3",
        title: "Lo implementamos juntos",
        description: "Resultados en semanas, no meses"
      }
    ],
    comparison: [
      { before: "Documentación manual", after: "Extracción automática" },
      { before: "Plazos perdidos", after: "100% cumplimiento" },
      { before: "Clientes llamando", after: "Respuestas 24/7" }
    ],
    finalCTA: {
      question: "¿Listo para procesar más sin errores?",
      options: [
        { text: "Agendar llamada", link: "/contacto" }
      ]
    }
  },
  asesorias: {
    hero: {
      title: "IA para asesorías.",
      subtitle: "Más valor. Menos rutina. Clientes más rentables."
    },
    problem: {
      bigStat: "El 70% de tu tiempo",
      context: "lo dedicas a tareas que no generan valor añadido."
    },
    blocks: [
      {
        title: "Informes en minutos",
        bigNumber: "-80% tiempo",
        comparison: "En elaboración de informes",
        description: "La IA analiza datos de tus clientes y genera informes personalizados. Tú aportas la estrategia.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Alertas proactivas",
        bigNumber: "Antes de que ocurra",
        comparison: "Detecta problemas",
        description: "Identifica desviaciones, oportunidades fiscales y riesgos. Tus clientes te ven como imprescindible.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Comunicación eficiente",
        bigNumber: "×3 clientes",
        comparison: "Atendidos con calidad",
        description: "Respuestas automáticas, recordatorios de documentación y seguimiento. Sin perder el toque personal.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      }
    ],
    caseStudy: {
      quote: "Pasamos de ser 'el que hace los impuestos' a ser asesores estratégicos. Y facturamos más por ello.",
      author: "Socio Director",
      role: "Asesoría fiscal",
      metrics: [
        { value: "+50%", label: "Facturación" },
        { value: "×2", label: "Clientes" },
        { value: "-70%", label: "Trabajo rutinario" }
      ]
    },
    process: [
      {
        number: "1",
        title: "Cuéntanos qué te frena",
        description: "Una llamada de 15 minutos"
      },
      {
        number: "2",
        title: "Te mostramos la solución",
        description: "Una demo con tu caso real"
      },
      {
        number: "3",
        title: "Lo implementamos juntos",
        description: "Resultados en semanas, no meses"
      }
    ],
    comparison: [
      { before: "Informes de días", after: "Informes de minutos" },
      { before: "Problemas detectados tarde", after: "Alertas proactivas" },
      { before: "Trabajo rutinario", after: "Asesoría estratégica" }
    ],
    finalCTA: {
      question: "¿Listo para ser más estratégico?",
      options: [
        { text: "Agendar llamada", link: "/contacto" }
      ]
    }
  },
  clinicas: {
    hero: {
      title: "IA para clínicas.",
      subtitle: "Más pacientes. Menos esperas. Mejor atención."
    },
    problem: {
      bigStat: "El 25% de las citas",
      context: "no se presentan porque nadie les recordó."
    },
    blocks: [
      {
        title: "Agenda siempre llena",
        bigNumber: "-80% no-shows",
        comparison: "Con recordatorios inteligentes",
        description: "La IA confirma citas, gestiona cancelaciones y rellena huecos automáticamente. Agenda optimizada sin esfuerzo.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Atención 24/7",
        bigNumber: "24/7",
        comparison: "Consultas y reservas",
        description: "Responde dudas, agenda citas y gestiona urgencias. Incluso fuera de horario.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Pacientes fidelizados",
        bigNumber: "+40%",
        comparison: "Retención de pacientes",
        description: "Recordatorios de revisiones, seguimiento post-tratamiento y comunicación personalizada. Automático.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      }
    ],
    caseStudy: {
      quote: "Los no-shows bajaron del 25% al 5%. Eso son cientos de huecos al mes que ahora facturamos.",
      author: "Director Médico",
      role: "Clínica dental",
      metrics: [
        { value: "-80%", label: "No-shows" },
        { value: "+30%", label: "Facturación" },
        { value: "+40%", label: "Retención" }
      ]
    },
    process: [
      {
        number: "1",
        title: "Cuéntanos qué te frena",
        description: "Una llamada de 15 minutos"
      },
      {
        number: "2",
        title: "Te mostramos la solución",
        description: "Una demo con tu caso real"
      },
      {
        number: "3",
        title: "Lo implementamos juntos",
        description: "Resultados en semanas, no meses"
      }
    ],
    comparison: [
      { before: "25% no-shows", after: "5% no-shows" },
      { before: "Atención solo en horario", after: "Atención 24/7" },
      { before: "Pacientes que no vuelven", after: "Fidelización automática" }
    ],
    finalCTA: {
      question: "¿Listo para llenar tu agenda?",
      options: [
        { text: "Agendar llamada", link: "/contacto" }
      ]
    }
  },
  hoteles: {
    hero: {
      title: "IA para hoteles.",
      subtitle: "Más reservas directas. Mejor experiencia. Mayor rentabilidad."
    },
    problem: {
      bigStat: "El 60% de reservas",
      context: "vienen de OTAs que se llevan el 20% de comisión."
    },
    blocks: [
      {
        title: "Reservas directas 24/7",
        bigNumber: "+40%",
        comparison: "Reservas sin comisión",
        description: "La IA atiende consultas, resuelve dudas y cierra reservas. En tu web, WhatsApp o teléfono. Sin intermediarios.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Huéspedes satisfechos",
        bigNumber: "+0.8 puntos",
        comparison: "En valoraciones online",
        description: "Comunicación proactiva, resolución rápida de incidencias y seguimiento personalizado. Antes de que opinen.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Operaciones optimizadas",
        bigNumber: "-50% tiempo",
        comparison: "En tareas de recepción",
        description: "Check-in automático, gestión de peticiones y coordinación con housekeeping. Todo fluye solo.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      }
    ],
    caseStudy: {
      quote: "Pasamos del 30% al 50% de reservas directas. Eso son miles de euros en comisiones ahorradas.",
      author: "Director General",
      role: "Hotel boutique",
      metrics: [
        { value: "+20%", label: "Reservas directas" },
        { value: "-15%", label: "Costes OTA" },
        { value: "+0.8", label: "Rating" }
      ]
    },
    process: [
      {
        number: "1",
        title: "Cuéntanos qué te frena",
        description: "Una llamada de 15 minutos"
      },
      {
        number: "2",
        title: "Te mostramos la solución",
        description: "Una demo con tu caso real"
      },
      {
        number: "3",
        title: "Lo implementamos juntos",
        description: "Resultados en semanas, no meses"
      }
    ],
    comparison: [
      { before: "60% reservas con comisión", after: "Más reservas directas" },
      { before: "Incidencias mal gestionadas", after: "Resolución proactiva" },
      { before: "Check-in manual", after: "Check-in automático" }
    ],
    finalCTA: {
      question: "¿Listo para aumentar reservas directas?",
      options: [
        { text: "Agendar llamada", link: "/contacto" }
      ]
    }
  },
  gimnasios: {
    hero: {
      title: "IA para gimnasios.",
      subtitle: "Más socios. Menos bajas. Comunidad activa."
    },
    problem: {
      bigStat: "El 50% de socios",
      context: "abandonan en los primeros 3 meses."
    },
    blocks: [
      {
        title: "Socios comprometidos",
        bigNumber: "-40% bajas",
        comparison: "Con seguimiento inteligente",
        description: "La IA detecta quién va a abandonar y actúa antes. Mensajes motivacionales, ofertas y atención personalizada.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Captación automática",
        bigNumber: "24/7",
        comparison: "Atención a interesados",
        description: "Responde consultas, agenda visitas y cierra inscripciones. Sin perder ningún lead por no contestar a tiempo.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Comunidad activa",
        bigNumber: "×3 engagement",
        comparison: "En redes y comunicación",
        description: "Contenido automático, retos, logros y comunicación que mantiene a los socios conectados.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      }
    ],
    caseStudy: {
      quote: "Las bajas se redujeron a la mitad. Ahora crecemos cada mes en lugar de solo reemplazar los que se van.",
      author: "Propietario",
      role: "Centro fitness",
      metrics: [
        { value: "-50%", label: "Bajas" },
        { value: "+30%", label: "Nuevos socios" },
        { value: "×2", label: "Permanencia" }
      ]
    },
    process: [
      {
        number: "1",
        title: "Cuéntanos qué te frena",
        description: "Una llamada de 15 minutos"
      },
      {
        number: "2",
        title: "Te mostramos la solución",
        description: "Una demo con tu caso real"
      },
      {
        number: "3",
        title: "Lo implementamos juntos",
        description: "Resultados en semanas, no meses"
      }
    ],
    comparison: [
      { before: "50% abandonan en 3 meses", after: "Retención mejorada" },
      { before: "Leads perdidos", after: "Captación 24/7" },
      { before: "Comunidad desconectada", after: "Engagement ×3" }
    ],
    finalCTA: {
      question: "¿Listo para reducir bajas?",
      options: [
        { text: "Agendar llamada", link: "/contacto" }
      ]
    }
  },
  academias: {
    hero: {
      title: "IA para academias.",
      subtitle: "Más alumnos. Mejor seguimiento. Resultados visibles."
    },
    problem: {
      bigStat: "El 35% de alumnos",
      context: "abandonan antes de terminar el curso."
    },
    blocks: [
      {
        title: "Alumnos que terminan",
        bigNumber: "-50% abandonos",
        comparison: "Con seguimiento personalizado",
        description: "La IA detecta alumnos en riesgo y actúa. Recordatorios, motivación y ayuda antes de que abandonen.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Atención escalable",
        bigNumber: "×5 consultas",
        comparison: "Resueltas sin más personal",
        description: "Dudas sobre horarios, precios, temarios. Todo respondido al instante, 24/7.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      },
      {
        title: "Marketing que funciona",
        bigNumber: "0 horas",
        comparison: "Creando contenido",
        description: "Posts, testimonios, campañas de captación. Todo generado y programado automáticamente.",
        ctaText: "Ver cómo funciona",
        ctaLink: "/contacto"
      }
    ],
    caseStudy: {
      quote: "Triplicamos las inscripciones y los abandonos bajaron a la mitad. La IA cambió nuestro negocio.",
      author: "Director",
      role: "Academia de idiomas",
      metrics: [
        { value: "×3", label: "Inscripciones" },
        { value: "-50%", label: "Abandonos" },
        { value: "+60%", label: "Facturación" }
      ]
    },
    process: [
      {
        number: "1",
        title: "Cuéntanos qué te frena",
        description: "Una llamada de 15 minutos"
      },
      {
        number: "2",
        title: "Te mostramos la solución",
        description: "Una demo con tu caso real"
      },
      {
        number: "3",
        title: "Lo implementamos juntos",
        description: "Resultados en semanas, no meses"
      }
    ],
    comparison: [
      { before: "35% abandonos", after: "Seguimiento personalizado" },
      { before: "Consultas sin responder", after: "Atención 24/7" },
      { before: "Marketing manual", after: "Contenido automático" }
    ],
    finalCTA: {
      question: "¿Listo para crecer tu academia?",
      options: [
        { text: "Agendar llamada", link: "/contacto" }
      ]
    }
  }
};

export default function NegocioDetail() {
  const { businessType } = useParams<{ businessType: string }>();
  
  // Normalizar el tipo de negocio eliminando acentos para la búsqueda
  const normalizedKey = normalizeUrl(businessType || "");
  const normalizedType = businessType?.charAt(0).toUpperCase() + businessType?.slice(1).toLowerCase();
  
  // Obtener configuración específica
  const config = businessConfig[normalizedKey];
  const seo = seoMeta[normalizedKey];
  
  if (!config || !normalizedType) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-8">
          <h1 className={`${typography.h2Large} text-foreground`}>Tipo de negocio no encontrado</h1>
          <Link 
            to="/negocios"
            className="group inline-flex items-center gap-4 text-foreground hover:opacity-60 transition-opacity"
          >
            <span className={typography.ctaLink}>Volver a Negocios</span>
            <span className="text-2xl md:text-3xl group-hover:translate-x-2 transition-transform">→</span>
          </Link>
        </div>
      </div>
    );
  }

  // Obtener artículos del tipo de negocio
  const { businessEntries } = getAllClassifiedTypes();
  const businessEntry = businessEntries.find(([type]) => type === normalizedType);
  const articleIds = businessEntry ? businessEntry[1] : [];
  
  // SEO: Actualizar meta tags dinámicamente
  useEffect(() => {
    if (seo) {
      document.title = seo.title;
      
      // Meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', seo.description);
      
      // Meta keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', seo.keywords);
      
      // Open Graph title
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', seo.title);
      
      // Open Graph description
      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (!ogDescription) {
        ogDescription = document.createElement('meta');
        ogDescription.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescription);
      }
      ogDescription.setAttribute('content', seo.description);
      
      // Canonical URL
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', `https://globaltis.com/negocios/${normalizedKey}`);
    }
  }, [seo, normalizedKey]);
  
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <AppleSection background="black" padding="large">
        <div className="text-center space-y-6 sm:space-y-8 md:space-y-12">
          <h1 className={`${typography.h1} text-white dark:text-black leading-tight px-4`}>
            {config.hero.title}
          </h1>
          <p className={`${typography.h2Medium} text-gray-400 dark:text-gray-600 px-4`}>
            {config.hero.subtitle}
          </p>
        </div>
      </AppleSection>

      {/* El Problema */}
      <AppleSection background="white" padding="large">
        <div className="text-center space-y-6 sm:space-y-8 md:space-y-12">
          <p className={`${typography.h1} text-foreground leading-none px-4`}>
            {config.problem.bigStat}
          </p>
          <p className={`${typography.h2Medium} text-muted-foreground max-w-5xl mx-auto leading-relaxed px-4`}>
            {config.problem.context}
          </p>
        </div>
      </AppleSection>

      {/* Bloques de Solución por Problema */}
      {config.blocks.map((block, index) => (
        <AppleSection 
          key={block.title}
          background={index % 2 === 0 ? "gray" : "white"} 
          padding="large"
        >
          <div className="max-w-6xl mx-auto space-y-8 md:space-y-16">
            <div className="text-center space-y-6 md:space-y-8">
              <h2 className={`${typography.h2Large} text-foreground px-4`}>
                {block.title}
              </h2>
              <div className="space-y-3 md:space-y-4">
                <p className={`${typography.h1} text-foreground`}>
                  {block.bigNumber}
                </p>
                <p className={`${typography.paragraphLarge} text-muted-foreground px-4`}>
                  {block.comparison}
                </p>
              </div>
              <p className={`${typography.paragraphNormal} text-muted-foreground max-w-3xl mx-auto leading-relaxed pt-4 md:pt-8 px-4`}>
                {block.description}
              </p>
            </div>

            {/* CTA del bloque */}
            <div className="flex justify-center pt-4 md:pt-8">
              <Link 
                to={block.ctaLink}
                className="group inline-flex items-center gap-3 md:gap-4 text-foreground hover:opacity-60 transition-opacity"
              >
                <span className={typography.ctaLink}>{block.ctaText}</span>
                <span className="text-xl sm:text-2xl md:text-3xl group-hover:translate-x-2 transition-transform">→</span>
              </Link>
            </div>
          </div>
        </AppleSection>
      ))}

      {/* Caso Real */}
      <AppleSection background="black" padding="large">
        <div className="max-w-4xl mx-auto space-y-8 md:space-y-12">
          <p className={`${typography.h2Medium} text-white dark:text-black text-center leading-relaxed px-4`}>
            "{config.caseStudy.quote}"
          </p>
          <div className="text-center space-y-2 px-4">
            <p className={`${typography.paragraphLarge} text-white dark:text-black font-medium`}>
              {config.caseStudy.author}
            </p>
            <p className={`${typography.paragraphBase} text-gray-400 dark:text-gray-600`}>
              {config.caseStudy.role}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 pt-8">
            {config.caseStudy.metrics.map((metric, index) => (
              <div key={index} className="text-center space-y-2">
                <p className={`${typography.h1} text-white dark:text-black`}>
                  {metric.value}
                </p>
                <p className={`${typography.paragraphBase} text-gray-400 dark:text-gray-600`}>
                  {metric.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </AppleSection>

      {/* Proceso */}
      <AppleSection background="white" padding="large">
        <div className="max-w-6xl mx-auto space-y-8 md:space-y-16">
          <h2 className={`${typography.h2Large} text-center text-foreground px-4`}>
            Cómo funciona
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {config.process.map((step) => (
              <div key={step.number} className="text-center space-y-4 md:space-y-6">
                <div className={`${typography.h1} text-foreground`}>
                  {step.number}
                </div>
                <h3 className={`${typography.paragraphLarge} text-foreground`}>
                  {step.title}
                </h3>
                <p className={`${typography.paragraphBase} text-muted-foreground`}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </AppleSection>

      {/* Comparativa 50/50 */}
      <AppleSplitComparison items={config.comparison} />

      {/* Sección de Informe IA */}
      <AppleSection background="white" padding="large">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center space-y-3 mb-10">
            <h2 className={`${typography.h2Medium} text-foreground`}>
              Tu informe de transformación digital
            </h2>
            <p className={`${typography.paragraphBase} text-muted-foreground`}>
              Personalizado para {normalizedType} en 30 segundos
            </p>
          </div>
          <BusinessReportForm 
            businessType={normalizedKey} 
            normalizedType={normalizedType} 
          />
        </div>
      </AppleSection>

      {/* CTA Final */}
      <AppleSection background="black" padding="large">
        <div className="text-center space-y-8 md:space-y-12 px-4">
          <h2 className={`${typography.h2Large} text-white dark:text-black`}>
            {config.finalCTA.question}
          </h2>
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 md:gap-12 justify-center items-center pt-8">
            {config.finalCTA.options.map((option, index) => (
              <Link 
                key={index}
                to={option.link}
                className="group inline-flex items-center gap-3 md:gap-4 text-white hover:opacity-60 transition-opacity"
              >
                <span className={typography.ctaLink}>{option.text}</span>
                <span className="text-xl sm:text-2xl md:text-3xl group-hover:translate-x-2 transition-transform">→</span>
              </Link>
            ))}
          </div>
        </div>
      </AppleSection>

      {/* Todos los artículos del sector */}
      <AppleSection background="white" padding="large">
        <div className="max-w-6xl mx-auto space-y-8 md:space-y-12">
          <h2 className={`${typography.h2Large} text-center text-foreground px-4`}>
            Todas las soluciones para {normalizedType}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
            {articleIds.map(articleId => {
              const article = blogPosts.find(p => p.id === articleId);
              if (!article) return null;
              
              return (
                <Link
                  key={articleId}
                  to={`/blog/${article.slug}`}
                  className="group space-y-3 md:space-y-4 hover:opacity-60 transition-opacity"
                >
                  <h3 className={`${typography.paragraphNormal} text-foreground line-clamp-2`}>
                    {article.title}
                  </h3>
                  <p className={`${typography.paragraphBase} text-muted-foreground line-clamp-2`}>
                    {article.description}
                  </p>
                  <div className="flex items-center gap-2 text-foreground pt-2">
                    <span className={typography.paragraphBase}>Ver más</span>
                    <span className="group-hover:translate-x-2 transition-transform">→</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </AppleSection>

      <Footer />
    </div>
  );
}
